import { getValidationResult, TQuestionWithAnswer, useEDCFlow, VALIDATION_STATUS } from "@smvtech/x-flux";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useEDCFlowContext } from "pages/EDCFlow/context";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import Mixpanel from "utils/mixpanel";
import DatePickerInput from "./DatePickerInput";
import RadioInput from "./RadioInput";
import SelectInput from "./SelectInput";
import TextInput from "./TextInput";
import TextareaInput from "./TextareaInput";
import { extractDefaultAnswer, processDropdownOptions } from "pages/EDCFlow/helper";

type QuestionItemProps = {
	question: TQuestionWithAnswer;
	applicationId: string;
	travellerId: string;
	disabled?: boolean;
	tooltip?: string;
};

const QuestionItem = ({ question, applicationId, travellerId, disabled = false, tooltip }: QuestionItemProps) => {
	const { updateAnswer, applicants } = useEDCFlow();
	const { setTncAccepted, setIsSavingAnswer } = useEDCFlowContext();

	const { processedOptions, optionLabelMap } = useMemo(() => {
		if (question.question.type !== "DROPDOWN") {
			return { processedOptions: question.question.options, optionLabelMap: {} };
		}

		return processDropdownOptions(question.question.options, applicants);
	}, [question.question.options, question.question.type, applicants]);

	const initialValue = (question.answer_data?.answer?.[0] ?? "").replace(/\n/g, ", ");
	const addDefaultValue = extractDefaultAnswer(initialValue);
	const isRequired = question.question.is_required;
	const questionText = question.question.question;

	const validationResult = useMemo(() => getValidationResult(question), [question]);

	const handleAnswerChange = async (value: string) => {
		if (!applicationId) return;

		setIsSavingAnswer(true);
		try {
			await updateAnswer(applicationId, question.question.type, question.question.question, {
				visa_question_id: question.id,
				traveller_id: travellerId,
				answer: value
			});
			Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.FORM_ANSWER_UPDATED, {
				applicationId,
				questionId: question.id,
				questionType: question.question.type
			});
		} catch (error) {
			console.error("Failed to update answer:", error);
		} finally {
			setTncAccepted(false);
			setIsSavingAnswer(false);
		}
	};

	const hasDefault = addDefaultValue != null;
	const addDefaultProps = hasDefault
		? { addDefaultValue, onAddDefault: () => handleAnswerChange(addDefaultValue!) }
		: {};

	const renderInput = () => {
		const commonProps = {
			disabled,
			initialValue,
			onChange: handleAnswerChange,
			questionText,
			hasError: validationResult?.status === VALIDATION_STATUS.UNFIXABLE,
			...addDefaultProps
		};

		let inputComponent;

		switch (question.question.type) {
			case "TEXT":
				inputComponent = <TextInput {...commonProps} />;
				break;
			case "TEXTAREA":
				inputComponent = <TextareaInput {...commonProps} />;
				break;
			case "SINGLE_CHOICE":
			case "DROPDOWN":
				if (processedOptions.length < 5) {
					inputComponent = (
						<RadioInput {...commonProps} options={processedOptions} optionLabelMap={optionLabelMap} />
					);
				} else {
					inputComponent = (
						<SelectInput {...commonProps} options={processedOptions} optionLabelMap={optionLabelMap} />
					);
				}
				break;
			case "CALENDAR":
				inputComponent = (
					<DatePickerInput
						{...commonProps}
						questionText={questionText}
						disabledDate={(currentDate) => {
							const lowerQuestion = questionText.toLowerCase();
							if (lowerQuestion.includes("date of issue")) {
								return currentDate && currentDate.isAfter(dayjs(), "day");
							}
							if (lowerQuestion.includes("date of expiry")) {
								return currentDate && currentDate.isBefore(dayjs(), "day");
							}
							return false;
						}}
					/>
				);
				break;
			default:
				return null;
		}

		if (tooltip) {
			return (
				<Tooltip title={tooltip}>
					<div className="inline-block w-full">{inputComponent}</div>
				</Tooltip>
			);
		}

		return inputComponent;
	};

	return (
		<div className="flex flex-col gap-2">
			<label
				htmlFor={`${applicationId}-${question.id}`}
				className={`text-sm font-medium ${validationResult?.status === VALIDATION_STATUS.UNFIXABLE ? "text-red-600" : ""}`}>
				{questionText}
				{isRequired && <span className="ml-1 text-red-500">*</span>}
			</label>
			{renderInput()}
			{validationResult?.status === VALIDATION_STATUS.UNFIXABLE && (
				<p className="text-xs text-red-600">
					{validationResult?.unfixable.items.map((item) => item.error_text).join(", ")}
				</p>
			)}
		</div>
	);
};

export default QuestionItem;

